import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { REPORT_CATALOG } from '../../config/reportCatalog';
import { fetchUserFavorites, createUserFavorite, deleteUserFavorite } from '../../graphql/reportQueries';
import { ReportTypeCard } from './ReportTypeCard';
import type { ReportCategory } from '../../types/reportTypes';
import { CollapsibleCard } from '../ui';
import '../../css/StandardReportsTab.css';

interface StandardReportsTabProps {
    companyId: string;
    userId: string;
}

interface CategoryConfig {
    key: string;
    label: string;
    reportCategory?: ReportCategory;
}

const CATEGORIES: CategoryConfig[] = [
    { key: 'favorites', label: 'Favorites' },
    { key: 'tire-maintenance', label: 'Tire Maintenance', reportCategory: 'tire-maintenance' },
    { key: 'pm-reports', label: 'PM Reports', reportCategory: 'pm-reports' },
    { key: 'cost-tracking', label: 'Cost Tracking', reportCategory: 'cost-tracking' },
    { key: 'safety', label: 'Safety', reportCategory: 'safety' },
];

/**
 * Standard Reports tab content.
 * Displays a searchable, categorized catalog of standard report types
 * with collapsible sections and favorite toggling.
 *
 * Validates: Requirements 2.1–2.8, 3.1, 3.3, 3.4, 3.5
 */
export const StandardReportsTab = ({ companyId, userId }: StandardReportsTabProps) => {
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(new Set());
    const [favoriteRecords, setFavoriteRecords] = useState<Map<string, string>>(new Map()); // reportTypeKey -> favoriteId
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set(CATEGORIES.map((c) => c.key)),
    );
    const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);

    // Load user favorites on mount
    useEffect(() => {
        if (!userId || !companyId) return;
        setIsLoadingFavorites(true);
        fetchUserFavorites(userId, companyId)
            .then((items) => {
                const keys = new Set<string>();
                const records = new Map<string, string>();
                items.forEach((item: any) => {
                    keys.add(item.reportTypeKey);
                    records.set(item.reportTypeKey, item.id);
                });
                setFavoriteKeys(keys);
                setFavoriteRecords(records);
            })
            .catch((err) => {
                console.error('Failed to load favorites:', err);
                Sentry.captureException(err);
            })
            .finally(() => setIsLoadingFavorites(false));
    }, [userId, companyId]);

    // Toggle favorite
    const handleToggleFavorite = useCallback(
        async (reportKey: string) => {
            if (favoriteKeys.has(reportKey)) {
                // Remove favorite
                const favoriteId = favoriteRecords.get(reportKey);
                if (favoriteId) {
                    setFavoriteKeys((prev) => {
                        const next = new Set(prev);
                        next.delete(reportKey);
                        return next;
                    });
                    setFavoriteRecords((prev) => {
                        const next = new Map(prev);
                        next.delete(reportKey);
                        return next;
                    });
                    try {
                        await deleteUserFavorite(favoriteId);
                    } catch (err) {
                        console.error('Failed to remove favorite:', err);
                        Sentry.captureException(err);
                        // Revert on error
                        setFavoriteKeys((prev) => new Set(prev).add(reportKey));
                        setFavoriteRecords((prev) => new Map(prev).set(reportKey, favoriteId));
                    }
                }
            } else {
                // Add favorite
                setFavoriteKeys((prev) => new Set(prev).add(reportKey));
                try {
                    const result = await createUserFavorite({
                        companyId,
                        userId,
                        reportTypeKey: reportKey,
                    });
                    if (!result) throw new Error('Failed to create user favorite.');
                    setFavoriteRecords((prev) => new Map(prev).set(reportKey, result.id));
                } catch (err) {
                    console.error('Failed to add favorite:', err);
                    Sentry.captureException(err);
                    // Revert on error
                    setFavoriteKeys((prev) => {
                        const next = new Set(prev);
                        next.delete(reportKey);
                        return next;
                    });
                }
            }
        },
        [favoriteKeys, favoriteRecords, companyId, userId],
    );

    const handleCardClick = useCallback(
        (reportKey: string) => {
            navigate(`/reports/standard/${reportKey}`);
        },
        [navigate],
    );

    // Filter reports by search query
    const filteredCatalog = useMemo(() => {
        if (!searchQuery.trim()) return REPORT_CATALOG;
        const query = searchQuery.toLowerCase();
        return REPORT_CATALOG.filter((entry) =>
            entry.displayName.toLowerCase().includes(query),
        );
    }, [searchQuery]);

    // Group reports by category
    const reportsByCategory = useMemo(() => {
        const map = new Map<string, typeof REPORT_CATALOG>();

        // Favorites
        const favReports = filteredCatalog.filter((entry) => favoriteKeys.has(entry.key));
        if (favReports.length > 0) {
            map.set('favorites', favReports);
        }

        // Other categories
        for (const cat of CATEGORIES) {
            if (cat.key === 'favorites') continue;
            const reports = filteredCatalog.filter((entry) => entry.category === cat.reportCategory);
            if (reports.length > 0) {
                map.set(cat.key, reports);
            }
        }

        return map;
    }, [filteredCatalog, favoriteKeys]);

    const hasResults = reportsByCategory.size > 0;

    if (isLoadingFavorites) {
        return <div className="standard-reports-loading">Loading reports...</div>;
    }

    return (
        <div className="standard-reports-tab">
            <div className="standard-reports-search">
                <span className="standard-reports-search-icon">🔍</span>
                <input
                    type="text"
                    className="standard-reports-search-input"
                    placeholder="Search reports..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search report types"
                />
            </div>

            {!hasResults ? (
                <div className="standard-reports-no-results">
                    No reports match "{searchQuery}"
                </div>
            ) : (
                <div className="standard-reports-categories">
                    {CATEGORIES.map((cat) => {
                        const reports = reportsByCategory.get(cat.key);
                        if (!reports || reports.length === 0) return null;

                        const isExpanded = expandedCategories.has(cat.key);

                        return (
                            <CollapsibleCard
                                key={cat.key}
                                className="standard-reports-category"
                                title={cat.label}
                                meta={`${reports.length} reports`}
                                expanded={isExpanded}
                                onExpandedChange={(expanded) => {
                                    setExpandedCategories((prev) => {
                                        const next = new Set(prev);
                                        if (expanded) next.add(cat.key);
                                        else next.delete(cat.key);
                                        return next;
                                    });
                                }}
                            >
                                <div className="standard-reports-grid">
                                    {reports.map((entry) => (
                                        <ReportTypeCard
                                            key={entry.key}
                                            reportKey={entry.key}
                                            displayName={entry.displayName}
                                            isFavorite={favoriteKeys.has(entry.key)}
                                            onToggleFavorite={handleToggleFavorite}
                                            onClick={handleCardClick}
                                        />
                                    ))}
                                </div>
                            </CollapsibleCard>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
